import type { ComponentType } from 'react';
import NotificationBellIcon from '@components/Icons/NotificationBell/NotificationBell';
import TwoPersonIcon from '@components/Icons/TwoPerson/TwoPerson';
import ClipboardIcon from '@components/Icons/Clipboard/Clipboard';
import DiamondIcon from '@components/Icons/Diamond/Diamond';
import ViewOffSlashIcon from '@components/Icons/ViewOffSlash/ViewOffSlash';
import NoOffersIcon from '@components/Icons/NoOffers/NoOffers';
import { JobAssignment } from '@th-types/job.detail.type';
import { JobAssignmentStatus } from '@th-types/job-assignment-status.type';
import { IJobApplicationCounters } from '@th-types/job-assignment.type';
import { JobPlacement } from '@company/types/job-placement.type';
import {
  NormalizedCandidateShape,
  CandidateStage,
  CandidateTag,
  JobCandidateDTO,
  SuggestedWorkerResult,
} from '@company/types/job-overview.type';

export type TabGroup = 'candidates' | 'offered' | 'hired';

export interface CandidatePanelTab {
  value: CandidateStage;
  group: TabGroup;
  Icon: ComponentType<{ size?: number; color?: string }>;
  items: unknown[];
  emptyLabel: string;
  emptyDescription: string;
}

/**
 * configured in the order they should appear in the UI,
 * this is for mobile navigation replacing tabs from desktop
 */
export const CANDIDATE_PANEL_TABS: CandidatePanelTab[] = [
  {
    value: CandidateStage.NEW,
    group: 'candidates',
    Icon: NotificationBellIcon,
    items: [],
    emptyLabel: 'No new candidates yet',
    emptyDescription: "When new applicants come in, they'll appear here.",
  },
  {
    value: CandidateStage.APPLICANTS,
    group: 'candidates',
    Icon: TwoPersonIcon,
    items: [],
    emptyLabel: 'No applicants to review right now',
    emptyDescription: 'All incoming applications will show up here.',
  },
  {
    value: CandidateStage.CONSIDERING,
    group: 'candidates',
    Icon: ClipboardIcon,
    items: [],
    emptyLabel: 'No candidates in consideration',
    emptyDescription:
      "Move candidates here when you're actively evaluating them.",
  },
  {
    value: CandidateStage.SUGGESTED,
    group: 'candidates',
    Icon: DiamondIcon,
    items: [],
    emptyLabel: 'No suggested candidates yet',
    emptyDescription:
      "We'll surface potential, and In-Network, matches here when available.",
  },
  {
    value: CandidateStage.HIDDEN,
    group: 'candidates',
    Icon: ViewOffSlashIcon,
    items: [],
    emptyLabel: 'Candidates you hide will show here for later.',
    emptyDescription:
      "Candidates you hide will show here for later. They don't know you've moved them here.",
  },
  {
    value: CandidateStage.OFFERED,
    group: 'offered',
    Icon: NoOffersIcon,
    items: [],
    emptyLabel: 'No offers sent yet',
    emptyDescription:
      'While you can hire direct, if you want to link them to the job to review later, or use our scheduling and check-in/out feature, send an offer to hire!',
  },
  {
    value: CandidateStage.HIRED,
    group: 'hired',
    Icon: NoOffersIcon,
    items: [],
    emptyLabel: 'No hired applicants yet',
    emptyDescription:
      "Once candidates are marked as hired, they'll be listed here.",
  },
];

// Map tab to the corresponding counter key in IJobApplicationCounters
const TAB_COUNTER: Partial<
  Record<CandidateStage, keyof IJobApplicationCounters>
> = {
  [CandidateStage.NEW]: 'newApplicants',
  [CandidateStage.APPLICANTS]: 'jobApplicants',
  [CandidateStage.CONSIDERING]: 'consideredWorkers',
  [CandidateStage.SUGGESTED]: 'suggestedWorkers',
  [CandidateStage.HIDDEN]: 'declinedAssignments',
  [CandidateStage.OFFERED]: 'jobOffers',
  [CandidateStage.HIRED]: 'hiredAssignments',
};

export const candidatePanelTabCount = (
  stage: CandidateStage,
  counters?: IJobApplicationCounters
): number | undefined => {
  const key = TAB_COUNTER[stage];
  return key ? (counters?.[key] ?? 0) : undefined;
};

export const candidatesGroupCount = (counters?: IJobApplicationCounters) =>
  (counters?.jobApplicants ?? 0) + (counters?.consideredWorkers ?? 0);

const HIRED_STATUSES: JobAssignmentStatus[] = [
  JobAssignmentStatus.HIRED,
  JobAssignmentStatus.TIMESHEET_COMPLETED,
  JobAssignmentStatus.TIMESHEET_APPROVED,
];

const APPLICANT_STATUSES: JobAssignmentStatus[] = [
  JobAssignmentStatus.WORKER_APPLIED,
  JobAssignmentStatus.WORKER_INTERESTED,
];

/**
 * Infer the candidate-panel tab for an assignment (email deep-link).
 * Hidden / hired / offered win over consider / new / applied flags.
 * Suggested is only SHARED workers who are not already on another tab.
 */
export const resolveCandidateStage = (
  jobAssignment: JobAssignment
): CandidateStage | null => {
  if (jobAssignment.hidden) {
    return CandidateStage.HIDDEN;
  }
  if (HIRED_STATUSES.includes(jobAssignment.status)) {
    return CandidateStage.HIRED;
  }
  if (jobAssignment.status === JobAssignmentStatus.OFFERED) {
    return CandidateStage.OFFERED;
  }
  if (jobAssignment.companyWorker?.isConsidered) {
    return CandidateStage.CONSIDERING;
  }
  if (jobAssignment.companyWorker?.isNewApplicant) {
    return CandidateStage.NEW;
  }
  if (APPLICANT_STATUSES.includes(jobAssignment.status)) {
    return CandidateStage.APPLICANTS;
  }
  if (jobAssignment.status === JobAssignmentStatus.SHARED) {
    return CandidateStage.SUGGESTED;
  }
  return null;
};

export const resolveCandidatePanelEmpty = (
  tab: CandidatePanelTab,
  isJobActive: boolean,
  restrictedAccess = false
): Pick<CandidatePanelTab, 'emptyLabel' | 'emptyDescription'> => {
  if (tab.value === CandidateStage.SUGGESTED && !isJobActive) {
    return {
      emptyLabel: 'Suggested workers are no longer shown for past jobs.',
      emptyDescription: '',
    };
  }
  if (tab.value === CandidateStage.SUGGESTED && restrictedAccess) {
    return {
      emptyLabel: 'This functionality is only available for premium members.',
      emptyDescription: '',
    };
  }
  return {
    emptyLabel: tab.emptyLabel,
    emptyDescription: tab.emptyDescription,
  };
};

interface TagInputs {
  matchSkills?: boolean;
  status?: JobAssignmentStatus;
  isNew?: boolean;
  hasApplied?: boolean;
  isSuggested?: boolean;
}

const buildTags = ({
  matchSkills,
  status,
  isNew,
  hasApplied,
  isSuggested,
}: TagInputs): CandidateTag[] => {
  const tags: CandidateTag[] = [];
  if (isNew) {
    tags.push({ label: 'NEW', variant: 'tagNew' });
  }
  const applied = hasApplied || status === JobAssignmentStatus.WORKER_APPLIED;
  if (applied) {
    tags.push({ label: 'APPLIED', variant: 'tagApplied' });
  } else if (isSuggested) {
    tags.push({ label: 'SUGGESTED', variant: 'tagSuggested' });
  }
  if (matchSkills) {
    tags.push({ label: 'MATCH', variant: 'tagMatch' });
  }
  return tags;
};

/**
 * Normalize responses from different tabs into a single NormalizedCandidateShape type for rendering in the UI.
 */
export const fromJobAssignment = (
  jobAssignment: JobAssignment
): NormalizedCandidateShape => {
  const considered = jobAssignment.companyWorker?.isConsidered;
  return {
    id: jobAssignment.worker.id,
    name: jobAssignment.worker.fullName,
    avatarUrl: jobAssignment.worker.profileImageUrl,
    miles: jobAssignment.distance,
    rating: jobAssignment.worker.rating,
    inNetwork: jobAssignment.worker.isPremium,
    favorite: jobAssignment.companyWorker?.isFavoritedByCompany,
    tags: buildTags({
      matchSkills: jobAssignment.matchSkills,
      status: jobAssignment.status,
      isNew: jobAssignment.companyWorker?.isNewApplicant,
    }),
    jobAssignmentId: jobAssignment.id,
    hidden: jobAssignment.hidden,
    considered,
  };
};

export const fromSuggestedResult = (
  result: SuggestedWorkerResult
): NormalizedCandidateShape => {
  const considered = result.isConsidered;
  return {
    id: result.worker.id,
    name: result.worker.fullName,
    avatarUrl: result.worker.profileImageUrl,
    miles: result.distance,
    rating: result.worker.rating,
    inNetwork: result.worker.isPremium,
    favorite: result.isFavoritedByCompany,
    tags: buildTags({
      matchSkills: result.matchSkills,
      status: result.jobAssignment?.status,
      isSuggested: true,
    }),
    jobAssignmentId: result.jobAssignment?.id ?? result.jobAssignmentId,
    hidden: result.jobAssignment?.hidden,
    considered,
  };
};

export const fromConsideredWorker = (
  worker: JobCandidateDTO
): NormalizedCandidateShape => ({
  id: worker.id,
  name: worker.fullName,
  avatarUrl: worker.profileImageUrl,
  miles: worker.distance,
  rating: worker.rating,
  inNetwork: worker.isPremium,
  favorite: undefined, // missing from BE
  tags: buildTags({
    isNew: worker.isNewApplicant,
    hasApplied: worker.hasApplied,
    isSuggested: !worker.hasApplied,
    matchSkills: worker.matchSkills,
  }),
  jobAssignmentId: worker.jobAssignmentId,
  considered: true,
});

export const fromJobPlacement = (
  placement: JobPlacement
): NormalizedCandidateShape => {
  const considered = placement.jobAssignment?.companyWorker?.isConsidered;
  return {
    id: placement.worker.id,
    name: placement.worker.fullName,
    avatarUrl: placement.worker.profileImageUrl,
    miles: placement.jobAssignment?.distance,
    rating: placement.worker.rating,
    inNetwork: placement.worker.isPremium,
    favorite: placement.jobAssignment?.companyWorker?.isFavoritedByCompany,
    tags: buildTags({
      matchSkills: placement.jobAssignment?.matchSkills,
      status: placement.jobAssignment?.status,
    }),
    jobAssignmentId: placement.jobAssignment?.id,
    hidden: placement.jobAssignment?.hidden,
    considered,
  };
};
